import { Button, Form, FormGroup } from "react-bootstrap";
import { HelpItem, HelpItemTranslation, emptyHelp, emptyHelpItemTranslation } from "../../interfaces/Help";
import { useState } from "react";
import CustomEditor from "../../elements/CustomEditor";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Overlay } from "../../elements/Overlay";
import { handleApiError } from "../../../utils/api";

export interface HelpAddProps {
    submitCallback: CallableFunction;
    closeCallback: CallableFunction;
}

export default function HelpAdd(props: HelpAddProps) {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const [helpItems, setHelpItems] = useState<HelpItem[]>([]);
    const [showAddHelpItemForm, setShowAddHelpItemForm] = useState(false);
    const [showEditHelpItemForm, setShowEditHelpItemForm] = useState(false);
    const [showDeleteHelpItemForm, setShowDeleteHelpItemForm] = useState(false);
    const [helpItem, setHelpItem] = useState<HelpItem>({
        _id: '',
        defaultLang: 'en',
        translations: []
    });
    const [helpItemIndex, setHelpItemIndex] = useState(-1);
    const [name, setName] = useState('');
    const [title, setTitle] = useState('');
    const [teaser, setTeaser] = useState('');
    const [body, setBody] = useState('');
    const [examples, setExamples] = useState('');
    const [references, setReferences] = useState('');

    const handleClose = () => {
        props.closeCallback();
    }

    const handleSubmit = (event: any) => {
        event.preventDefault();
        let error = false;
        let error_messages: string[] = [];

        if (error) {
            alert(error_messages.join('\n'));
        } else {
            const newHelp = Object.assign({}, emptyHelp);
            newHelp.name = event.target.name.value;
            // Remove _id from items if empty (for new items)
            newHelp.items = helpItems.map((hi: HelpItem) => {
                const item = { ...hi };
                if (item._id === '') {
                    delete (item as any)._id;
                }
                return item;
            });
            newHelp.weight = parseInt(event.target.weight.value) || 0;

            // Remove _id from Help if empty (for new Help)
            if (newHelp._id === '') {
                delete (newHelp as any)._id;
            }

            const requestOptionsPost = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                },
                body: JSON.stringify(newHelp)
            };
            fetch(import.meta.env.VITE_API_URL + '/v1/help', requestOptionsPost)
                .then(async response => {
                    if (handleApiError(response, navigate)) {
                        return null;
                    }

                    // API returns the created Help object as JSON
                    const text = await response.text();
                    try {
                        return JSON.parse(text);
                    } catch (e) {
                        console.error('JSON parse error:', e, 'Text:', text);
                        alert('Error parsing server response');
                        return null;
                    }
                })
                .then(data => {
                    if (!data) return;
                    props.submitCallback(data);
                });
        }
    }

    const handleShowAddHelpItemForm = () => {
        setTitle('');
        setTeaser('');
        setBody('');
        setExamples('');
        setReferences('');
        setShowAddHelpItemForm(true);
    }

    const handleHideAddHelpItemForm = () => {
        setShowAddHelpItemForm(false);
    }

    const handleShowEditHelpItemForm = (event: any, index: number) => {
        event.preventDefault();
        setHelpItemIndex(index);
        const editItem = helpItems[index];
        // Get English translation
        const enTranslation = editItem.translations.find(t => t.lang === 'en') || editItem.translations[0];
        if (enTranslation) {
            setTitle(enTranslation.title);
            setTeaser(enTranslation.teaser);
            setBody(enTranslation.body);
            setExamples(enTranslation.examples);
            setReferences(enTranslation.references);
        }
        setHelpItem(editItem);
        setShowEditHelpItemForm(true);
    }

    const handleHideEditHelpItemForm = () => {
        setShowEditHelpItemForm(false);
    }

    const handleShowDeleteHelpItemForm = (event: any, index: number) => {
        event.preventDefault();
        setHelpItemIndex(index);
        const newHelpItem = Object.assign({}, helpItems[index]);
        setHelpItem(newHelpItem);
        setShowDeleteHelpItemForm(true);
    }

    const handleHideDeleteHelpItemForm = () => {
        setShowDeleteHelpItemForm(false);
    }

    const handleAddHelpItemSubmit = (event: any) => {
        event.preventDefault();

        // Create translation with English content
        const baseTranslation: HelpItemTranslation = {
            lang: 'en',
            title: title,
            teaser: teaser,
            body: body,
            examples: examples,
            references: references
        };

        // Duplicate to all languages (en, es, ca, ru) with the same English content
        const allTranslations: HelpItemTranslation[] = [
            { ...baseTranslation, lang: 'en' },
            { ...baseTranslation, lang: 'es' },
            { ...baseTranslation, lang: 'ca' },
            { ...baseTranslation, lang: 'ru' }
        ];

        const newHelpItem: HelpItem = {
            _id: '',
            defaultLang: 'en',
            translations: allTranslations
        };

        const newHelpItems = helpItems.map((hi: HelpItem) => { return(hi); });
        newHelpItems.push(newHelpItem);
        setHelpItems(newHelpItems);
        setShowAddHelpItemForm(false);
    }

    const handleEditHelpItemSubmit = (event: any) => {
        event.preventDefault();

        // Update English translation, keep others
        const updatedTranslations = helpItem.translations.map(trans => {
            if (trans.lang === 'en') {
                return {
                    ...trans,
                    title: title,
                    teaser: teaser,
                    body: body,
                    examples: examples,
                    references: references
                };
            }
            return trans;
        });

        const newHelpItem: HelpItem = {
            ...helpItem,
            translations: updatedTranslations
        };

        const newHelpItems = helpItems.map((hi: HelpItem, hiIndex: number) => {
            if (hiIndex === helpItemIndex) {
                return newHelpItem;
            } else {
                return(hi);
            }
        });
        setHelpItems(newHelpItems);
        setShowEditHelpItemForm(false);
    }

    const handleDeleteHelpItemSubmit = (event: any) => {
        event.preventDefault();
        const newHelpItems = helpItems.filter((hi: HelpItem, index: number) => index !== helpItemIndex);
        setHelpItems(newHelpItems);
        setShowDeleteHelpItemForm(false);
    }

    const handleTeaserChange = (data: string) => {
        setTeaser(data);
    }

    const handleBodyChange = (data: string) => {
        setBody(data);
    }

    const handleExamplesChange = (data: string) => {
        setExamples(data);
    }

    const handleReferencesChange = (data: string) => {
        setReferences(data);
    }

    const getHelpItemTitle = (item: HelpItem): string => {
        const enTranslation = item.translations.find(t => t.lang === 'en');
        return enTranslation?.title || item.translations[0]?.title || t("Help item");
    }

    const doNothing = () => {

    }

    return(
        <Overlay title={t("Add new help")} onClose={handleClose} className="admin-add-help-container">
            <div className={ 'help-item-form-container' + ((showAddHelpItemForm || showEditHelpItemForm || showDeleteHelpItemForm) ? ' hide' : '') }>
                            <Form onSubmit={ handleSubmit }>
                                <FormGroup className="name" controlId="name">
                                    <Form.Label aria-required={ true }>{ t("Name") }</Form.Label>
                                    <Form.Control type="text" required={ true } defaultValue={ name }
                                        placeholder={ t("Enter a descriptive name") } />
                                </FormGroup>
                                <FormGroup className="weight" controlId="weight">
                                    <Form.Label>{ t("Weight") }</Form.Label>
                                    <Form.Control type="number" defaultValue={ 0 } />
                                    <Form.Text id="weightHelpBlock" muted>
                                        { t("Enter the position of this help content relative to other help contents. The higher the value the lower it will be sorted.") }
                                    </Form.Text>
                                </FormGroup>
                                <div className="help-items">
                                    <strong>{ t("Help items") }</strong>
                                    { helpItems.length === 0 && !showAddHelpItemForm && !showEditHelpItemForm && !showDeleteHelpItemForm &&
                                        <div className="no-items-message">
                                            { t("There are no help items defined. Add your first help item.") }
                                        </div>
                                    }
                                    { helpItems.length > 0 && !showAddHelpItemForm && !showEditHelpItemForm && !showDeleteHelpItemForm &&
                                        <div className="help-items-list">
                                            { helpItems.map((hi: HelpItem, index: number) => {
                                                return(
                                                    <div className="help-item-elem" key={index}>
                                                        <div className="title">
                                                            { getHelpItemTitle(hi) }
                                                        </div>
                                                        <div className="help-item-actions">
                                                            <a className="edit" title={ t("Edit help") }
                                                                href="#" onClick={ (event) => handleShowEditHelpItemForm(event, index) }></a>
                                                            <a className="delete" title={ t("Delete user") }
                                                                href="#" onClick={ (event) => handleShowDeleteHelpItemForm(event, index) }></a>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    }
                                    <div className="help-item-actions">
                                        <Button variant="secondary" onClick={ handleShowAddHelpItemForm }>
                                            { t("Add help item") }
                                        </Button>
                                    </div>
                                </div>
                                <div className="actions">
                                    <Button type="submit" variant="primary">{ t("Save") }</Button>
                                </div>
                            </Form>
                        </div>
                        { showAddHelpItemForm &&
                            <Form onSubmit={ handleAddHelpItemSubmit } className="add-help-item">
                                <h5>{ t('Add help Item') } (English)</h5>
                                <FormGroup className="title" controlId="title">
                                    <Form.Label>{ t("Title") }</Form.Label>
                                    <Form.Control type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                                        placeholder={ t("Enter the title") } />
                                </FormGroup>
                                <div className="field field-teaser">
                                    <CustomEditor
                                        change={ handleTeaserChange }
                                        changeFormatted={ doNothing }
                                        data={ teaser }
                                        formatted={ '' }
                                        id="teaser"
                                        label={ t("Teaser") }
                                        npl={ false }
                                        task_id={ '' }
                                    />
                                </div>
                                <div className="field field-body">
                                    <CustomEditor
                                        change={ handleBodyChange }
                                        changeFormatted={ doNothing }
                                        data={ body }
                                        formatted={ '' }
                                        id="body"
                                        label={ t("Body")}
                                        npl={ false }
                                        task_id={ '' }
                                    />
                                </div>
                                <div className="field field-examples">
                                    <CustomEditor
                                        change={ handleExamplesChange }
                                        changeFormatted={ doNothing }
                                        data={ examples }
                                        formatted={ '' }
                                        id="examples"
                                        label={ t("Examples")}
                                        npl={ false }
                                        task_id={ '' }
                                    />
                                </div>
                                <div className="field field-references">
                                    <CustomEditor
                                        change={ handleReferencesChange }
                                        changeFormatted={ doNothing }
                                        data={ references }
                                        formatted={ '' }
                                        id="references"
                                        label={ t("References")}
                                        npl={ false }
                                        task_id={ '' }
                                    />
                                </div>
                                <div className="actions">
                                    <Button type="submit" variant="primary">{ t("Save") }</Button>
                                    <Button onClick={ handleHideAddHelpItemForm } variant="warning">{ t("Cancel") }</Button>
                                </div>
                            </Form>
                        }
                        { showEditHelpItemForm &&
                            <Form onSubmit={ handleEditHelpItemSubmit } className="edit-help-item">
                                <h5>{ t('Edit Help Item') } (English)</h5>
                                <FormGroup className="title" controlId="title">
                                    <Form.Label>{ t("Title") }</Form.Label>
                                    <Form.Control type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                                        placeholder={ t("Enter the title") } />
                                </FormGroup>
                                <div className="field field-teaser">
                                    <CustomEditor
                                        change={ handleTeaserChange }
                                        changeFormatted={ doNothing }
                                        data={ teaser }
                                        formatted={ '' }
                                        id="teaser"
                                        label={ t("Teaser") }
                                        npl={ false }
                                        task_id={ '' }
                                    />
                                </div>
                                <div className="field field-body">
                                    <CustomEditor
                                        change={ handleBodyChange }
                                        changeFormatted={ doNothing }
                                        data={ body }
                                        formatted={ '' }
                                        id="body"
                                        label={ t("Body")}
                                        npl={ false }
                                        task_id={ '' }
                                    />
                                </div>
                                <div className="field field-examples">
                                    <CustomEditor
                                        change={ handleExamplesChange }
                                        changeFormatted={ doNothing }
                                        data={ examples }
                                        formatted={ '' }
                                        id="examples"
                                        label={ t("Examples")}
                                        npl={ false }
                                        task_id={ '' }
                                    />
                                </div>
                                <div className="field field-references">
                                    <CustomEditor
                                        change={ handleReferencesChange }
                                        changeFormatted={ doNothing }
                                        data={ references }
                                        formatted={ '' }
                                        id="references"
                                        label={ t("References")}
                                        npl={ false }
                                        task_id={ '' }
                                    />
                                </div>
                                <div className="actions">
                                    <Button type="submit" variant="primary">{ t("Modify") }</Button>
                                    <Button onClick={ handleHideEditHelpItemForm } variant="warning">{ t("Cancel") }</Button>
                                </div>
                            </Form>
                        }
                        { showDeleteHelpItemForm &&
                            <Form onSubmit={ handleDeleteHelpItemSubmit } className="delete-help-item">
                                <h5>{ t('Delete help item') }</h5>
                                <FormGroup className="confirmation-message" controlId="confirmation">
                                    <Form.Text id="confirmationMessage">
                                        { t("Are you sure you want to delete Help Item named?") } <strong>{ getHelpItemTitle(helpItem) }</strong>? { t("This action cannot be undone.") }
                                    </Form.Text>
                                </FormGroup>
                                <div className="actions">
                                    <Button type="submit" variant="primary">{ t("Delete") }</Button>
                                    <Button onClick={ handleHideDeleteHelpItemForm } variant="warning">{ t("Cancel") }</Button>
                                </div>
                            </Form>
                        }
        </Overlay>
    );
}
